// Wannlabs cross-site sign-in, PKCE-style (server: build.wannlabs.com builder-api account/ssoCodes.ts).
// Prime keeps a random verifier in this tab, hops to wannlabs.com/app with only S256(verifier), and gets back a
// 60 s, single-use code bound to "prime" in the #fragment. /auth/wannlabs swaps code + verifier for a one-time
// sign-in token (in the response body), which the Prime API redeems as before. No token is ever put in a URL.
export const WL_CENTRAL = "https://wannlabs.com/app/";
export const WL_EXCHANGE = "https://build.wannlabs.com/api/public/sso/exchange";
const PENDING = "prime-wl-sso:";

const b64u = (b: ArrayBuffer | Uint8Array): string => {
  let s = "";
  for (const x of new Uint8Array(b as ArrayBuffer)) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const rand = (n: number) => b64u(crypto.getRandomValues(new Uint8Array(n)));
export async function s256(v: string): Promise<string> {
  return b64u(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v)));
}
export function hasWannlabsHint(cookie: string = document.cookie): boolean {
  return /(?:^|;\s*)wl_signed_in=1(?:;|$)/.test(cookie);
}

/** Top-level hop to the central Wannlabs sign-in. `next` is a same-site Prime path to land on. */
export async function startWannlabsSso(next = "", go = (u: string) => window.location.replace(u)): Promise<void> {
  const verifier = rand(32);
  const state = rand(16);
  const challenge = await s256(verifier);
  const safe = /^\/(?!\/)[^\s\\]*$/.test(next) ? next : "";
  sessionStorage.setItem(PENDING + state, JSON.stringify({ v: verifier, next: safe }));
  go(`${WL_CENTRAL}#wl_sso_to=prime&c=${challenge}&s=${state}`);
}

export type HandoffResult = { kind: "none" } | { kind: "nosession" } | { kind: "token"; tokenHash: string; next: string } | { kind: "failed" };

/** /auth/wannlabs: read #wl_code=..&s=.. (or #wl_nosession=1&s=..) and swap it for a one-time token. */
export async function finishWannlabsSso(hash: string, f: typeof fetch = (...a) => fetch(...a)): Promise<HandoffResult> {
  const h = new URLSearchParams(hash.replace(/^#/, ""));
  const state = h.get("s") ?? "";
  const code = h.get("wl_code");
  if (!state) return { kind: "none" };
  let p: { v?: string; next?: string } | null = null;
  try {
    p = JSON.parse(sessionStorage.getItem(PENDING + state) || "null");
    sessionStorage.removeItem(PENDING + state);
  } catch {
    p = null;
  }
  if (!code) return h.has("wl_nosession") ? { kind: "nosession" } : { kind: "none" };
  if (!p?.v) return { kind: "failed" };
  const r = await f(WL_EXCHANGE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, verifier: p.v, aud: "prime" }),
    credentials: "omit",
    cache: "no-store",
  }).catch(() => null);
  const j = (r && r.ok ? await r.json().catch(() => null) : null) as { tokenHash?: string } | null;
  return j?.tokenHash ? { kind: "token", tokenHash: j.tokenHash, next: p.next ?? "" } : { kind: "failed" };
}
